import { useEffect, useState } from 'react'
import { getKpis, getPLBars, getTrendDaily, getTopProducts, getTopProductsMonthly, getLowStock, getApAging, getArAging } from '../api/dashboard'
import { Link } from 'react-router-dom'
import { useSession } from '../session'
import { getIncomeStatementGaap } from '../api/accounting'
import { StatementTable, incomeRows } from './accounting/reportsShared'
import MultiBarChart from '../components/MultiBarChart'
import { me as getMe } from '../api/auth'
import Modal from '../components/Modal'
import Button from '../components/Button'
import Spinner from '../components/Spinner'
import ErrorBanner from '../components/ErrorBanner'
import PageHeader from '../components/PageHeader'
import { companyNow, localToday } from '../utils/format'

function Card({ title, value }) {
  return (
    <div className="kpi-card">
      <div className="value">
        {typeof value === 'number'
          ? value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
          : value}
      </div>
      <div className="label">{title}</div>
    </div>
  )
}

function Bar({ label, value, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((Math.abs(value) / max) * 100)) : 0
  const color =
    label === 'Revenue'
      ? '#3b82f6'
      : label === 'Cost of revenue'
        ? '#ef4444'
        : '#10b981'
  return (
    <div className="pl-row">
      <div className="pl-row-head">
        <div className="pl-row-label">{label}</div>
        <div className="pl-row-value">
          {Number(value || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
        </div>
      </div>
      <div className="pl-track">
        <div className="pl-fill" style={{ width: pct + '%', background: color }} />
      </div>
    </div>
  )
}

const fmt2 = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const right = { textAlign: 'right' }
const bold = { textAlign: 'right', fontWeight: 600 }
const AGE_KEYS = ['current', 'd30', 'd60', 'd90', 'd90p']

// Open balances by vendor or customer, aged by how long past due they are.
function AgingPanel({ title, party, rows, totals, idKey, nameKey, link, empty, toolbar }) {
  return (
    <div className="card panel dashboard-card section">
      <div className="card-header dashboard-header">
        <div className="section-title dashboard-title">{title}</div>
        {toolbar}
      </div>
      <table className="table table-sm dashboard-table">
        <thead>
          <tr>
            <th>{party}</th>
            <th style={right}>Balance</th>
            <th style={right}>Not yet due</th>
            <th style={right}>1–30</th>
            <th style={right}>31–60</th>
            <th style={right}>61–90</th>
            <th style={right}>&gt;90</th>
            <th style={right}>Oldest (days)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r[idKey] ? <Link to={link(r[idKey])}>{r[nameKey] || r[idKey]}</Link> : (r[nameKey] || r[idKey])}</td>
              <td style={right}>{fmt2(r.balance)}</td>
              {AGE_KEYS.map((k) => <td key={k} style={right}>{fmt2(r[k])}</td>)}
              <td style={right}>{r.days_outstanding}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="8" className="muted">{empty}</td></tr>}
        </tbody>
        <tfoot>
          <tr>
            <td style={bold}>Totals</td>
            <td style={bold}>{fmt2(totals?.balance)}</td>
            {AGE_KEYS.map((k) => <td key={k} style={bold}>{fmt2(totals?.[k])}</td>)}
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

// The detailed sales and supply analysis that used to be the dashboard; the
// Overview links here for anyone who wants to dig in.
export default function Analysis() {
  // Roles: gate sections for non-admin departments
  const [roles, setRoles] = useState([])

  useEffect(() => {
    ; (async () => {
      try {
        const u = await getMe()
        const arr = (u?.roles || (u?.role ? [u.role] : [])).map((r) =>
          String(r || '').toLowerCase()
        )
        setRoles(arr)
      } catch {
        setRoles([])
      }
    })()
  }, [])

  const isAdmin = roles.includes('admin')
  const isAccounting = roles.includes('accounting') || roles.includes('accountant')
  const isPurch = roles.includes('purchase') || roles.includes('purchaser')
  const isInventory = roles.includes('inventory')
  const { has } = useSession()
  const canSeeFinance = (isAdmin || isAccounting) && has('accounting')
  const canSeeSupply = (isAdmin || isPurch || isInventory) && has('inventory')

  // Filters/controls
  const [asOf, setAsOf] = useState(() => localToday())
  const [period, setPeriod] = useState('mtd')
  const [tpLimit, setTpLimit] = useState(10)
  const [tpStart, setTpStart] = useState('')
  const [tpEnd, setTpEnd] = useState('')
  const [lsThreshold, setLsThreshold] = useState(5)

  // 🎯 NEW: Legend toggle state for main trend chart
  const [hiddenSeries, setHiddenSeries] = useState(new Set())

  // 🎯 NEW: Click-to-filter state
  const [selectedPeriod, setSelectedPeriod] = useState(null)
  const [selectedPeriodLabel, setSelectedPeriodLabel] = useState(null)

  // 🎯 NEW: Drill-down modal state
  const [drillDownData, setDrillDownData] = useState(null)
  const [loadingDrillDown, setLoadingDrillDown] = useState(false)

  // Toggle series visibility
  const toggleSeries = (seriesName) => {
    setHiddenSeries(prev => {
      const newSet = new Set(prev)
      if (newSet.has(seriesName)) {
        newSet.delete(seriesName)
      } else {
        newSet.add(seriesName)
      }
      return newSet
    })
  }

  // 🎯 NEW: Handle bar click - filter tables and show drill-down
  const handleBarClick = async (label, index) => {
    console.log('📊 Bar clicked:', label, 'index:', index)

    // Get the date range for this period
    const dateKey = trend.keys?.[index]
    if (!dateKey) return

    const periodRange = calculatePeriodRange(dateKey, trendGran)
    setSelectedPeriod(periodRange)
    setSelectedPeriodLabel(label)

    // Filter top products for this period
    try {
      const tp = await getTopProducts({
        start: periodRange.start,
        end: periodRange.end,
        limit: tpLimit
      })
      setTopProducts(tp || [])
    } catch (e) {
      console.error('Failed to filter top products:', e)
    }

    // Load drill-down data
    loadDrillDownData(periodRange, label)
  }

  // 🎯 NEW: Calculate date range based on granularity
  const calculatePeriodRange = (dateKey, granularity) => {
    const pad = (n) => String(n).padStart(2, '0')
    const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

    if (granularity === 'daily') {
      return { start: dateKey, end: dateKey }
    }

    if (granularity === 'weekly') {
      const start = new Date(dateKey + 'T00:00:00')
      const end = new Date(start)
      end.setDate(end.getDate() + 6)
      return { start: toISO(start), end: toISO(end) }
    }

    if (granularity === 'biweekly') {
      const start = new Date(dateKey + 'T00:00:00')
      const end = new Date(start)
      end.setDate(end.getDate() + 13)
      return { start: toISO(start), end: toISO(end) }
    }

    if (granularity === 'monthly') {
      // dateKey format: YYYY-MM
      const [year, month] = dateKey.split('-').map(Number)
      const start = new Date(year, month - 1, 1)
      const end = new Date(year, month, 0) // Last day of month
      return { start: toISO(start), end: toISO(end) }
    }

    return { start: dateKey, end: dateKey }
  }

  // 🎯 NEW: Load drill-down data
  const loadDrillDownData = async (periodRange, label) => {
    setLoadingDrillDown(true)
    setDrillDownData({ label, loading: true })

    try {
      const [income, topProds, plBars] = await Promise.all([
        withTimeout(
          getIncomeStatementGaap({ start: periodRange.start, end: periodRange.end, compare: 'none' }),
          15000
        ).catch(() => null),
        withTimeout(
          getTopProducts({ start: periodRange.start, end: periodRange.end, limit: 10 }),
          15000
        ).catch(() => []),
        withTimeout(
          getPLBars({ period: 'custom', start: periodRange.start, end: periodRange.end }),
          15000
        ).catch(() => ({ revenue: 0, cogs: 0, gross_profit: 0 }))
      ])

      setDrillDownData({
        label,
        period: periodRange,
        income,
        topProducts: topProds,
        plBars,
        loading: false
      })
    } catch (error) {
      console.error('Drill-down load error:', error)
      setDrillDownData({ label, error: error.message, loading: false })
    } finally {
      setLoadingDrillDown(false)
    }
  }

  // 🎯 NEW: Clear filter
  const clearFilter = () => {
    setSelectedPeriod(null)
    setSelectedPeriodLabel(null)
    // Reload original data
    refreshAll()
  }

  // Data
  const [kpis, setKpis] = useState(null)
  const [kpiBaselines, setKpiBaselines] = useState({
    prevDay: null,
    prevMonthEnd: null,
    prevYearSameDate: null
  })
  const [plBars, setPlBars] = useState({
    revenue: 0,
    cogs: 0,
    gross_profit: 0,
    net_income: 0
  })
  const [trend, setTrend] = useState({
    labels: [],
    revenue: [],
    cogs: [],
    gross_profit: []
  })
  const [trendNI, setTrendNI] = useState([])
  const [trendGran, setTrendGran] = useState('monthly') // daily | weekly | biweekly | monthly
  const [topProducts, setTopProducts] = useState([])
  const [tpQuery, setTpQuery] = useState('')

  // Top products by month (seasonality)
  const _initTpm = (() => {
    const end = companyNow()
    const start = new Date(end.getFullYear(), end.getMonth() - 11, 1)
    const pad = (n) => String(n).padStart(2, '0')
    const toISO = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    return { start: toISO(start), end: toISO(end) }
  })()
  const [tpmStart, setTpmStart] = useState(_initTpm.start)
  const [tpmEnd, setTpmEnd] = useState(_initTpm.end)
  const [tpmLimit, setTpmLimit] = useState(5)
  const [tpmMetric, setTpmMetric] = useState('revenue') // 'qty' | 'revenue'
  const [tpmGran, setTpmGran] = useState('monthly') // 'daily' | 'weekly' | 'biweekly' | 'monthly'
  const [tpmQuery, setTpmQuery] = useState('')
  const [topMonthly, setTopMonthly] = useState({
    labels: [],
    series: [],
    metric: 'revenue'
  })
  const [lowStock, setLowStock] = useState([])
  const [apAging, setApAging] = useState({
    vendors: [],
    totals: { balance: 0, current: 0, d30: 0, d60: 0, d90: 0, d90p: 0 }
  })
  const [arAging, setArAging] = useState({
    customers: [],
    totals: { balance: 0, current: 0, d30: 0, d60: 0, d90: 0, d90p: 0 }
  })
  const [error, setError] = useState('')

  const withTimeout = (p, ms) =>
    Promise.race([
      p,
      new Promise((_, rej) => setTimeout(() => rej(new Error('Request timeout')), ms))
    ])

  // Load on mount
  useEffect(() => {
    refreshAll()
  }, [canSeeFinance, canSeeSupply])

  const rangeForPeriod = (asOfStr, kind) => {
    const asOf = new Date(asOfStr)
    const pad = (n) => String(n).padStart(2, '0')
    const toISO = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    if (kind === 'today') return { start: toISO(asOf), end: toISO(asOf) }
    if (kind === 'mtd') {
      const start = new Date(asOf.getFullYear(), asOf.getMonth(), 1)
      return { start: toISO(start), end: toISO(asOf) }
    }
    if (kind === 'ytd') {
      const start = new Date(asOf.getFullYear(), 0, 1)
      return { start: toISO(start), end: toISO(asOf) }
    }
    if (kind === 'last30') {
      const start = new Date(asOf)
      start.setDate(start.getDate() - 29)
      return { start: toISO(start), end: toISO(asOf) }
    }
    return { start: toISO(asOf), end: toISO(asOf) }
  }

  const ytdRange = (asOfStr) => {
    const asOf = new Date(asOfStr)
    const pad = (n) => String(n).padStart(2, '0')
    const toISO = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    const start = new Date(asOf.getFullYear(), 0, 1)
    return { start: toISO(start), end: toISO(asOf) }
  }

  const prevMonthEnd = (asOfStr) => {
    const asOfDate = new Date(`${asOfStr}T00:00:00`)
    const pad = (n) => String(n).padStart(2, '0')
    const d = new Date(asOfDate.getFullYear(), asOfDate.getMonth(), 0)
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  }

  const prevDay = (asOfStr) => {
    const asOfDate = new Date(`${asOfStr}T00:00:00`)
    const pad = (n) => String(n).padStart(2, '0')
    asOfDate.setDate(asOfDate.getDate() - 1)
    return `${asOfDate.getFullYear()}-${pad(asOfDate.getMonth() + 1)}-${pad(asOfDate.getDate())}`
  }

  const priorYearSameDay = (asOfStr) => {
    const asOfDate = new Date(`${asOfStr}T00:00:00`)
    const y = asOfDate.getFullYear() - 1
    const m = asOfDate.getMonth()
    const d = asOfDate.getDate()
    const maxDay = new Date(y, m + 1, 0).getDate()
    const day = Math.min(d, maxDay)
    const pad = (n) => String(n).padStart(2, '0')
    return `${y}-${pad(m + 1)}-${pad(day)}`
  }

  const refreshAll = async () => {
    try {
      if (canSeeFinance) {
        const { start, end } = rangeForPeriod(asOf, period)

        // 🐛 DEBUG: Log the date ranges being used
        console.log('🔍 Dashboard Date Ranges:', {
          asOf,
          period,
          rangeForPeriod: { start, end },
          ytdRange: ytdRange(asOf)
        })

        // Load critical finance data with timeouts
        const [k, kPrevDay, kPrevMonthEnd, kPrevYear, p, t, ap, ar] = await Promise.all([
          withTimeout(getKpis({ as_of: asOf }), 15000).catch(err => {
            console.error('KPIs failed:', err)
            return null
          }),

          withTimeout(getKpis({ as_of: prevDay(asOf) }), 15000).catch(err => {
            console.error('Prev-day KPIs failed:', err)
            return null
          }),

          withTimeout(getKpis({ as_of: prevMonthEnd(asOf) }), 15000).catch(err => {
            console.error('Prev-month KPIs failed:', err)
            return null
          }),

          withTimeout(getKpis({ as_of: priorYearSameDay(asOf) }), 15000).catch(err => {
            console.error('Prior-year KPIs failed:', err)
            return null
          }),

          (() => {
            const r = rangeForPeriod(asOf, period)
            return withTimeout(
              getPLBars({ period, start: r.start, end: r.end }),
              15000
            ).catch(err => {
              console.error('PL Bars failed:', err)
              return { revenue: 0, cogs: 0, gross_profit: 0 }
            })
          })(),

          (() => {
            const r = ytdRange(asOf)
            console.log('📊 Fetching trend data for YTD:', r)
            return withTimeout(
              getTrendDaily({ start: r.start, end: r.end }),
              15000
            ).catch(err => {
              console.error('Trend failed:', err)
              return { labels: [], revenue: [], cogs: [], gross_profit: [], net_income: [] }
            })
          })(),

          withTimeout(
            getApAging({ as_of: asOf, limit: 10 }),
            15000
          ).catch(err => {
            console.error('AP Aging failed:', err)
            return { vendors: [], totals: {} }
          }),

          withTimeout(
            getArAging({ as_of: asOf, limit: 10 }),
            15000
          ).catch(err => {
            console.error('AR Aging failed:', err)
            return { customers: [], totals: {} }
          })
        ])

        // 🐛 DEBUG: Log the raw trend data
        console.log('📈 Raw Trend Data:', t)
        console.log('📈 Trend labels:', t?.labels)
        console.log('📈 Trend revenue:', t?.revenue)

        setKpis(k)
        setKpiBaselines({
          prevDay: kPrevDay,
          prevMonthEnd: kPrevMonthEnd,
          prevYearSameDate: kPrevYear
        })
        // Net income comes with the bars, from the income statement.
        setPlBars(p)
        const agg = aggregateTrend(t, trendGran)

        // 🐛 DEBUG: Log the aggregated data
        console.log('📊 Aggregated Trend Data:', agg)
        console.log('📊 Aggregation granularity:', trendGran)

        setTrend(agg)
        setTrendNI(agg.net_income || [])
        setApAging(ap || { vendors: [], totals: {} })
        setArAging(ar || { customers: [], totals: {} })
      } else {
        setKpis(null)
        setKpiBaselines({
          prevDay: null,
          prevMonthEnd: null,
          prevYearSameDate: null
        })
        setPlBars({
          revenue: 0,
          cogs: 0,
          gross_profit: 0,
          net_income: 0
        })
        setTrend({
          labels: [],
          revenue: [],
          cogs: [],
          gross_profit: []
        })
        setTrendNI([])
        setApAging({
          vendors: [],
          totals: { balance: 0, current: 0, d30: 0, d60: 0, d90: 0, d90p: 0 }
        })
        setArAging({
          customers: [],
          totals: { balance: 0, current: 0, d30: 0, d60: 0, d90: 0, d90p: 0 }
        })
      }
      if (canSeeSupply) {
        const [ls, tp, tpm] = await Promise.all([
          getLowStock({ threshold: lsThreshold }),
          getTopProducts({ days: 30, limit: tpLimit }),
          getTopProductsMonthly({
            start: tpmStart,
            end: tpmEnd,
            limit: tpmLimit,
            metric: tpmMetric,
            granularity: tpmGran,
            ...(tpmQuery ? { q: tpmQuery } : {})
          })
        ])
        setLowStock(ls)
        setTopProducts(tp || [])
        setTopMonthly(tpm || {
          labels: [],
          series: [],
          metric: tpmMetric
        })
      } else {
        setLowStock([])
        setTopProducts([])
        setTopMonthly({
          labels: [],
          series: [],
          metric: tpmMetric
        })
      }
      setError('')
    } catch (e) {
      console.error('Dashboard refresh error:', e)
      setError(String(e?.response?.data?.error || e?.message || e))
    }
  }

  const refreshKpis = async () => {
    try {
      const [k, kPrevDay, kPrevMonthEnd, kPrevYear] = await Promise.all([
        getKpis({ as_of: asOf }),
        getKpis({ as_of: prevDay(asOf) }),
        getKpis({ as_of: prevMonthEnd(asOf) }),
        getKpis({ as_of: priorYearSameDay(asOf) })
      ])
      setKpis(k || null)
      setKpiBaselines({
        prevDay: kPrevDay || null,
        prevMonthEnd: kPrevMonthEnd || null,
        prevYearSameDate: kPrevYear || null
      })
    } catch (e) {
      setError(String(e?.response?.data?.error || e?.message || e))
    }
  }

  const refreshPL = async () => {
    try {
      const { start, end } = rangeForPeriod(asOf, period)
      const [p, td] = await Promise.all([
        getPLBars({ period, start, end }),
        (() => {
          const r = ytdRange(asOf)
          return getTrendDaily({ start: r.start, end: r.end })
        })()
      ])
      setPlBars(p)
      const agg = aggregateTrend(td, trendGran)
      setTrend(agg)
      setTrendNI(agg?.net_income || [])
    } catch (e) {
      setError(String(e?.response?.data?.error || e?.message || e))
    }
  }

  // Aggregate daily trend to requested granularity
  const aggregateTrend = (daily, gran) => {
    const d =
      daily || {
        labels: [],
        revenue: [],
        cogs: [],
        gross_profit: [],
        net_income: []
      };
    if (gran === 'daily') return d;
    const parse = (s) => new Date(s + 'T00:00:00');
    const toISO = (dt) => dt.toISOString().slice(0, 10);
    const toMD = (dt) => `${dt.getMonth() + 1}/${dt.getDate()}`;
    const bins = {};
    const weekStartISO = (dateObj) => {
      const dow = (dateObj.getDay() + 6) % 7; // Mon=0
      const start = new Date(dateObj);
      start.setDate(dateObj.getDate() - dow);
      return toISO(start);
    };
    const monthKey = (dateObj) =>
      `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(
        2,
        '0'
      )}`;
    for (let i = 0; i < d.labels.length; i++) {
      const dt = parse(d.labels[i]);
      let key;
      if (gran === 'weekly' || gran === 'biweekly') key = weekStartISO(dt);
      else if (gran === 'monthly') key = monthKey(dt);
      else continue;

      if (!bins[key]) bins[key] = { key, revenue: 0, cogs: 0, gp: 0, ni: 0 };
      bins[key].revenue += Number(d.revenue[i] || 0);
      bins[key].cogs += Number(d.cogs[i] || 0);
      bins[key].gp += Number(d.gross_profit[i] || 0);
      bins[key].ni += Number(d.net_income?.[i] || 0);
    }
    let entries = Object.values(bins);
    entries.sort((a, b) => new Date(a.key) - new Date(b.key));
    if (gran === 'biweekly') {
      const merged = [];
      for (let i = 0; i < entries.length; i += 2) {
        const a = entries[i];
        const b = entries[i + 1];
        if (!b) {
          merged.push(a);
        } else {
          merged.push({
            key: a.key,
            revenue: a.revenue + b.revenue,
            cogs: a.cogs + b.cogs,
            gp: a.gp + b.gp,
            ni: a.ni + b.ni
          });
        }
      }
      entries = merged;
    } else if (gran === 'weekly') {
      entries = entries.map((e) => ({
        ...e,
        label: toMD(new Date(e.key))
      }));
    } else if (gran === 'monthly') {
      // 🐛 FIX: Parse month key correctly to avoid timezone issues
      entries = entries.map((e) => {
        // e.key is in format YYYY-MM
        const [year, month] = e.key.split('-').map(Number)
        return {
          ...e,
          label: `${month}/${year}`
        }
      });
    }
    return {
      labels: entries.map((e) => e.label ?? toMD(new Date(e.key))),
      keys: entries.map((e) => e.key),
      revenue: entries.map((e) => Number(e.revenue.toFixed(2))),
      cogs: entries.map((e) => Number(e.cogs.toFixed(2))),
      gross_profit: entries.map((e) => Number(e.gp.toFixed(2))),
      net_income: entries.map((e) => Number(e.ni.toFixed(2)))
    };
  };

  // trend auto-refreshes via refreshAll; no parameters needed
  const refreshTop = async () => {
    try {
      setTopProducts(await getTopProducts({ days: 30, limit: tpLimit }))
    } catch (e) {
      setError(String(e?.response?.data?.error || e?.message || e))
    }
  }

  const runTopMonthly = async () => {
    try {
      const params = {
        start: tpmStart,
        end: tpmEnd,
        limit: tpmLimit,
        metric: tpmMetric
      }
      if (tpmQuery) params.q = tpmQuery
      const d = await getTopProductsMonthly(params)
      setTopMonthly(
        d || { labels: [], series: [], metric: tpmMetric }
      )
    } catch (e) {
      setError(String(e?.response?.data?.error || e?.message || e))
    }
  }

  const refreshLow = async () => {
    try {
      setLowStock(await getLowStock({ threshold: lsThreshold }))
    } catch (e) {
      setError(String(e?.response?.data?.error || e?.message || e))
    }
  }

  const refreshAging = async () => {
    try {
      const [ap, ar] = await Promise.all([getApAging({ as_of: asOf, limit: 10 }), getArAging({ as_of: asOf, limit: 10 })])
      setApAging(ap)
      setArAging(ar)
    } catch (e) {
      setError(String(e?.response?.data?.error || e?.message || e))
    }
  }

  if (error)
    return (
      <div className="dashboard-v2">
        <div className="dashboard-empty-state">
          <ErrorBanner message={error} />
        </div>
      </div>
    )

  if (canSeeFinance && !kpis)
    return (
      <div className="dashboard-v2">
        <div className="dashboard-empty-state">
          <Spinner label="Loading…" />
        </div>
      </div>
    )

  const barMax = Math.max(
    Math.abs(plBars.revenue),
    Math.abs(plBars.cogs),
    Math.abs(plBars.gross_profit),
    Math.abs(plBars.net_income),
    1
  )
  const formatMoney = (value) =>
    Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  const asOfDisplay = (() => {
    try {
      return new Date(`${asOf}T00:00:00`).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    } catch {
      return asOf
    }
  })()
  const prevMonthLabel = (() => {
    try {
      const d = new Date(`${asOf}T00:00:00`)
      d.setMonth(d.getMonth() - 1)
      return d.toLocaleDateString('en-US', { month: 'short' })
    } catch {
      return 'last month'
    }
  })()
  const changeMeta = (currentRaw, baselineRaw, label, fallbackText) => {
    const current = Number(currentRaw || 0)
    const baseline = Number(baselineRaw)
    if (!Number.isFinite(baseline) || Math.abs(baseline) < 1e-9) {
      return { deltaClass: 'delta-flat', deltaText: fallbackText || `No ${label} baseline` }
    }
    const pct = ((current - baseline) / Math.abs(baseline)) * 100
    const deltaClass =
      pct > 0.05 ? 'delta-up' : pct < -0.05 ? 'delta-down' : 'delta-flat'
    return {
      deltaClass,
      deltaText: `${pct >= 0 ? '↑' : '↓'} ${Math.abs(pct).toFixed(1)}% ${label}`
    }
  }

  const cashMeta = changeMeta(
    kpis?.cash_position,
    kpiBaselines?.prevMonthEnd?.cash_position,
    'vs last month',
    `As of ${asOfDisplay}`
  )
  const todayMeta = changeMeta(
    kpis?.sales_today,
    kpiBaselines?.prevDay?.sales_today,
    'vs yesterday',
    'No prior day sales'
  )
  const mtdMeta = changeMeta(
    kpis?.sales_mtd,
    kpiBaselines?.prevMonthEnd?.sales_mtd,
    `vs ${prevMonthLabel}`,
    `No ${prevMonthLabel} baseline`
  )
  const ytdMeta = changeMeta(
    kpis?.sales_ytd,
    kpiBaselines?.prevYearSameDate?.sales_ytd,
    'vs prior YTD',
    'No prior-year baseline'
  )

  const kpiCards = [
    {
      key: 'cash',
      label: 'Cash Position',
      value: kpis?.cash_position,
      tone: '',
      deltaClass: cashMeta.deltaClass,
      deltaText: cashMeta.deltaText
    },
    {
      key: 'today',
      label: 'Revenue Today',
      value: kpis?.sales_today,
      tone: 'blue',
      deltaClass: todayMeta.deltaClass,
      deltaText: todayMeta.deltaText
    },
    {
      key: 'mtd',
      label: 'Revenue MTD',
      value: kpis?.sales_mtd,
      tone: 'green',
      deltaClass: mtdMeta.deltaClass,
      deltaText: mtdMeta.deltaText
    },
    {
      key: 'ytd',
      label: 'Revenue YTD',
      value: kpis?.sales_ytd,
      tone: 'blue',
      deltaClass: ytdMeta.deltaClass,
      deltaText: ytdMeta.deltaText
    }
  ]

  // export helpers
  const downloadCsv = (filename, headers, rows) => {
    const csv = [
      headers.join(','),
      ...rows.map((r) =>
        r
          .map((x) => {
            const v = (x ?? '').toString()
            return /[",\n]/.test(v)
              ? '"' + v.replace(/"/g, '""') + '"'
              : v
          })
          .join(',')
      )
    ].join('\n')
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const exportTop = () =>
    downloadCsv(
      `dashboard_top_products_${tpStart || 'last30'}_${tpEnd || ''
      }_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`,
      ['Prod ID', 'SKU', 'Name', 'Qty', 'Revenue'],
      topProducts.map((r) => [
        r.product_id,
        r.sku,
        r.name,
        r.qty_sold,
        r.revenue
      ])
    )

  const runTop = async () => {
    try {
      const params = { limit: tpLimit }
      if (tpStart && tpEnd) {
        params.start = tpStart
        params.end = tpEnd
      } else {
        params.days = 30
      }
      if (tpQuery) params.q = tpQuery
      const rows = await getTopProducts(params)
      setTopProducts(rows || [])
    } catch (e) {
      setError(String(e?.response?.data?.error || e?.message || e))
    }
  }

  const exportLow = () =>
    downloadCsv(
      `dashboard_low_stock_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, '-')}.csv`,
      ['Prod ID', 'SKU', 'Name', 'On Hand', 'Reorder'],
      lowStock.map((r) => [
        r.product_id,
        r.sku,
        r.name,
        r.qty_on_hand,
        r.reorder_level ?? ''
      ])
    )

  return (
    <div className="dashboard-v2">
      <div className="dashboard-shell">
        <PageHeader
          title="Sales analysis"
          subtitle="Trends, top sellers, stock and supplier balances in detail"
          breadcrumbs={<Link to="/dashboard">Overview</Link>}
        />

        {canSeeFinance && (
          <div className="card panel section dashboard-overview-card">
            <div className="card-header dashboard-header dashboard-main-header">
              <div className="section-title dashboard-title">Overview</div>
              <div className="toolbar dashboard-toolbar">
                <label>As of</label>
                <input
                  type="date"
                  value={asOf}
                  onChange={(e) => setAsOf(e.target.value)}
                />
                <button className="btn" onClick={refreshKpis}>
                  Refresh KPIs
                </button>
                <button className="btn btn-primary" onClick={refreshAll}>
                  Refresh All
                </button>
              </div>
            </div>
            <div className="dashboard-kpi-grid">
              {kpiCards.map((item) => (
                <div key={item.key} className="dashboard-kpi-cell">
                  <div className="dashboard-kpi-label">{item.label}</div>
                  <div className={`dashboard-kpi-value ${item.tone}`}>{formatMoney(item.value)}</div>
                  <span className={`dashboard-kpi-delta ${item.deltaClass}`}>{item.deltaText}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Middle row: P&L bars + Trend chart */}
        {canSeeFinance && (
          <div className="section">
            <div className="card panel dashboard-card">
          <div className="card-header dashboard-header">
            <div className="dashboard-title">
              Revenue / Cost of revenue / Gross Profit / Net Income
            </div>
            <div className="toolbar dashboard-toolbar">
              <label>Period</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="mtd">MTD</option>
                <option value="ytd">YTD</option>
                <option value="last30">Last 30</option>
              </select>
              <button className="btn" onClick={refreshPL}>
                Run
              </button>
              <label>Granularity</label>
              <select
                value={trendGran}
                onChange={async (e) => {
                  const g = e.target.value
                  setTrendGran(g)
                  const { start, end } = ytdRange(asOf)
                  try {
                    const td = await getTrendDaily({ start, end })
                    const agg = aggregateTrend(td, g)
                    setTrend(agg)
                    setTrendNI(agg.net_income || [])
                  } catch { }
                }}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <Bar label="Revenue" value={plBars.revenue} max={barMax} />
            <Bar label="Cost of revenue" value={plBars.cogs} max={barMax} />
            <Bar
              label="Gross Profit"
              value={plBars.gross_profit}
              max={barMax}
            />
            <Bar
              label="Net Income"
              value={plBars.net_income}
              max={barMax}
            />
          </div>
          <div style={{ marginTop: 16 }}>
            {/* 🎯 Clickable Legend */}
            <div className="dashboard-legend" style={{ justifyContent: 'center', marginBottom: 12 }}>
              {[
                { key: 'Revenue', label: 'Revenue', color: '#3b82f6' },
                { key: 'Cost of revenue', label: 'Cost of revenue', color: '#ef4444' },
                { key: 'GP', label: 'GP', color: '#10b981' },
                { key: 'Net Income', label: 'Net Income', color: '#22c55e' }
              ].map(({ key, label, color }) => (
                <span
                  key={key}
                  onClick={() => toggleSeries(key)}
                  style={{
                    cursor: 'pointer',
                    opacity: hiddenSeries.has(key) ? 0.4 : 1,
                    transition: 'opacity 0.2s ease',
                    textDecoration: hiddenSeries.has(key) ? 'line-through' : 'none',
                    userSelect: 'none'
                  }}
                  title={hiddenSeries.has(key) ? `Click to show ${label}` : `Click to hide ${label}`}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: color,
                      marginRight: 6,
                      borderRadius: '2px'
                    }}
                  />
                  {label}
                </span>
              ))}
            </div>
            <MultiBarChart
              height={300}
              yTicks={8}
              showPointLabels={false}
              paddingLeft={15}
              paddingRight={2}
              labels={trend.labels}
              hiddenSeries={hiddenSeries}
              maxXTicks={
                trendGran === 'weekly' || trendGran === 'biweekly'
                  ? 1000
                  : trendGran === 'daily'
                    ? 10
                    : 10
              }
              xLabelFormatter={(s, i) => {
                // Smart ticks: for weekly/biweekly, only show month when month changes
                if (trendGran === 'weekly' || trendGran === 'biweekly') {
                  const key = (trend.keys || [])[i] || '' // YYYY-MM-DD week start
                  const prevKey = (trend.keys || [])[i - 1] || ''
                  if (!key) return ''
                  const [y, m] = key.split('-')
                  const [py, pm] = (prevKey || '').split('-')
                  if (i === 0 || m !== pm || y !== py) {
                    const mNum = parseInt(m, 10)
                    const names = [
                      '',
                      'Jan',
                      'Feb',
                      'Mar',
                      'Apr',
                      'May',
                      'Jun',
                      'Jul',
                      'Aug',
                      'Sep',
                      'Oct',
                      'Nov',
                      'Dec'
                    ]
                    return names[mNum]
                  }
                  return ''
                }
                // format YYYY-MM-DD -> M/D, YYYY-MM -> M/YY, else as-is
                if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
                  const [y, m, d] = s.split('-')
                  return parseInt(m, 10) + '/' + parseInt(d, 10)
                }
                if (/^\d{4}-\d{2}$/.test(s)) {
                  const [y, m] = s.split('-')
                  return parseInt(m, 10) + '/' + String(y).slice(2)
                }
                return s
              }}
              series={[
                { name: 'Revenue', color: '#3b82f6', values: trend.revenue },
                { name: 'Cost of revenue', color: '#ef4444', values: trend.cogs },
                { name: 'GP', color: '#10b981', values: trend.gross_profit },
                { name: 'Net Income', color: '#22c55e', values: trendNI }
              ]}
              onBarClick={handleBarClick}
            />
          </div>
        </div>
          </div>
        )}

      {/* Payables and receivables aging */}
      {canSeeFinance && (
        <>
          <AgingPanel title="Payables aging: open bills by vendor" party="Vendor" rows={apAging?.vendors || []} totals={apAging?.totals}
            idKey="vendor_id" nameKey="vendor_name" link={(id) => `/purchase/ap/vendor/${id}`} empty="No open bills."
            toolbar={(
              <div className="toolbar dashboard-toolbar">
                <label>As of</label>
                <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
                <button className="btn" onClick={refreshAging}>Refresh</button>
              </div>
            )} />
          <AgingPanel title="Receivables aging: open invoices by customer" party="Customer" rows={arAging?.customers || []} totals={arAging?.totals}
            idKey="customer_id" nameKey="customer_name" link={(id) => `/master/customers/${id}`} empty="No open invoices."
            toolbar={<div className="toolbar dashboard-toolbar"><span className="muted">As of {asOf}</span></div>} />
        </>
      )}

      {/* Top Products by Month (seasonality) */}
      {canSeeSupply && (
        <>
          <div className="card panel dashboard-card section">
        <div
          className="card-header dashboard-header"
          style={{ justifyContent: 'space-between' }}
        >
          <div className="section-title dashboard-title">Top Products by Month</div>
          <div className="toolbar dashboard-toolbar">
            <label>Start</label>
            <input
              type="date"
              value={tpmStart}
              onChange={(e) => setTpmStart(e.target.value)}
              style={{ width: 150 }}
            />
            <label>End</label>
            <input
              type="date"
              value={tpmEnd}
              onChange={(e) => setTpmEnd(e.target.value)}
              style={{ width: 150 }}
            />
            <label>Top N</label>
            <input
              style={{ width: 80 }}
              value={tpmLimit}
              onChange={(e) => setTpmLimit(e.target.value)}
            />
            <label>Filter</label>
            <input
              placeholder="Name or SKU"
              style={{ width: 180 }}
              value={tpmQuery}
              onChange={(e) => setTpmQuery(e.target.value)}
            />
            <label>Metric</label>
            <select
              value={tpmMetric}
              onChange={async (e) => {
                const m = e.target.value
                setTpmMetric(m)
                try {
                  const d = await getTopProductsMonthly({
                    start: tpmStart,
                    end: tpmEnd,
                    limit: tpmLimit,
                    metric: m,
                    granularity: tpmGran,
                    ...(tpmQuery ? { q: tpmQuery } : {})
                  })
                  setTopMonthly(
                    d || { labels: [], series: [], metric: m }
                  )
                } catch { }
              }}
            >
              <option value="revenue">Revenue</option>
              <option value="qty">Qty</option>
            </select>
            <label>Granularity</label>
            <select
              value={tpmGran}
              onChange={async (e) => {
                const g = e.target.value
                setTpmGran(g)
                try {
                  const d = await getTopProductsMonthly({
                    start: tpmStart,
                    end: tpmEnd,
                    limit: tpmLimit,
                    metric: tpmMetric,
                    granularity: g,
                    ...(tpmQuery ? { q: tpmQuery } : {})
                  })
                  setTopMonthly(
                    d || { labels: [], series: [], metric: tpmMetric }
                  )
                } catch { }
              }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <button className="btn" onClick={runTopMonthly}>
              Run
            </button>
          </div>
        </div>
        {(() => {
          const palette = [
            '#3b82f6',
            '#ef4444',
            '#10b981',
            '#f59e0b',
            '#7c3aed',
            '#06b6d4',
            '#f97316',
            '#22c55e',
            '#e11d48',
            '#8b5cf6'
          ]
          const rawSeries = Array.isArray(topMonthly?.series)
            ? topMonthly.series
            : []
          const series = rawSeries.map((s, i) => ({
            name:
              s?.name ||
              s?.sku ||
              s?.product_id ||
              `Series ${i + 1}`,
            color: palette[i % palette.length],
            values: Array.isArray(s?.values) ? s.values : []
          }))
          const labels = Array.isArray(topMonthly?.labels)
            ? topMonthly.labels
            : []
          return (
            <>
              <MultiBarChart
                height={300}
                yTicks={6}
                showPointLabels={false}
                paddingLeft={15}
                paddingRight={2}
                labels={labels}
                xLabelFormatter={(s) =>
                  /^\d{4}-\d{2}$/.test(s)
                    ? (() => {
                      const [y, m] = s.split('-')
                      return (
                        parseInt(m, 10) +
                        '/' +
                        String(y).slice(2)
                      )
                    })()
                    : /^\d{4}-\d{2}-\d{2}$/.test(s)
                      ? (() => {
                        const [y, m, d] = s.split('-')
                        return parseInt(m, 10) + '/' + parseInt(d, 10)
                      })()
                      : s
                }
                yFormatter={(n) =>
                  tpmMetric === 'qty'
                    ? Number(n || 0).toLocaleString(undefined, {
                      maximumFractionDigits: 0
                    })
                    : Number(n || 0).toLocaleString(
                      undefined,
                      Math.abs(Number(n || 0)) >= 1000
                        ? { maximumFractionDigits: 0 }
                        : {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }
                    )
                }
                series={series}
              />
              <div className="dashboard-legend">
                {series.map((s, i) => (
                  <span key={i}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        background: s.color,
                        marginRight: 6
                      }}
                    />
                    {s.name}
                  </span>
                ))}
                {series.length === 0 && <span>No data.</span>}
              </div>
            </>
          )
        })()}
          </div>

          {/* Bottom row: Top products + Low stock */}
          <div className="two-col section">
        {/* 🎯 Filter Indicator Banner */}
        {selectedPeriod && (
          <div style={{
            gridColumn: '1 / -1',
            marginBottom: 16,
            padding: '12px 16px',
            background: '#eff6ff',
            border: '1px solid #3b82f6',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '18px' }}>🔍</span>
              <div>
                <div style={{ fontWeight: 600, color: '#1e40af' }}>
                  Filtered by: {selectedPeriodLabel}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  {selectedPeriod.start} to {selectedPeriod.end}
                </div>
              </div>
            </div>
            <button
              className="btn btn-ghost"
              onClick={clearFilter}
              style={{ fontSize: '13px' }}
            >
              ✕ Clear Filter
            </button>
          </div>
        )}

        <div className="card panel dashboard-card">
          <div className="card-header dashboard-header">
            <div className="dashboard-title">Top Products</div>
            <div className="toolbar dashboard-toolbar">
              <label>Start</label>
              <input
                type="date"
                value={tpStart}
                onChange={(e) => setTpStart(e.target.value)}
                style={{ width: 150 }}
              />
              <label>End</label>
              <input
                type="date"
                value={tpEnd}
                onChange={(e) => setTpEnd(e.target.value)}
                style={{ width: 150 }}
              />
              <label>Limit</label>
              <input
                style={{ width: 80 }}
                value={tpLimit}
                onChange={(e) => setTpLimit(e.target.value)}
              />
              <label>Filter</label>
              <input
                placeholder="Name or SKU"
                style={{ width: 180 }}
                value={tpQuery}
                onChange={(e) => setTpQuery(e.target.value)}
              />
              <button className="btn" onClick={runTop}>
                Run
              </button>
              <button className="btn btn-ghost" onClick={exportTop}>
                Export CSV
              </button>
            </div>
          </div>
          <table className="table table-sm dashboard-table">
            <thead>
              <tr>
                <th>Prod ID</th>
                <th>SKU</th>
                <th>Name</th>
                <th>Qty</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {topProducts.map((r, i) => (
                <tr key={i}>
                  <td>{r.product_id}</td>
                  <td>{r.sku}</td>
                  <td>{r.name}</td>
                  <td style={{ textAlign: 'right' }}>{r.qty_sold}</td>
                  <td style={{ textAlign: 'right' }}>
                    {Number(r.revenue || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                </tr>
              ))}
              {topProducts.length === 0 && (
                <tr>
                  <td colSpan="5" className="muted">
                    No sales found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card panel dashboard-card">
          <div className="card-header dashboard-header">
            <div className="dashboard-title">Low Stock</div>
            <div className="toolbar dashboard-toolbar">
              <label>Threshold</label>
              <input
                style={{ width: 100 }}
                value={lsThreshold}
                onChange={(e) => setLsThreshold(e.target.value)}
              />
              <button className="btn" onClick={refreshLow}>
                Run
              </button>
              <button className="btn btn-ghost" onClick={exportLow}>
                Export CSV
              </button>
            </div>
          </div>
          <table className="table table-sm dashboard-table">
            <thead>
              <tr>
                <th>Prod ID</th>
                <th>SKU</th>
                <th>Name</th>
                <th>On Hand</th>
                <th>Reorder</th>
              </tr>
            </thead>
            <tbody>
              {lowStock.map((r, i) => (
                <tr key={i}>
                  <td>{r.product_id}</td>
                  <td>{r.sku}</td>
                  <td>{r.name}</td>
                  <td style={{ textAlign: 'right' }}>
                    {Number(r.qty_on_hand || 0).toFixed(0)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {r.reorder_level == null ? '' : r.reorder_level}
                  </td>
                </tr>
              ))}
              {lowStock.length === 0 && (
                <tr>
                  <td colSpan="5" className="muted">
                    No products running low.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
          </div>
        </>
      )}

      {/* 🎯 Drill-Down Modal */}
      <Modal
        open={!!drillDownData}
        onClose={() => setDrillDownData(null)}
        title={`Period Details: ${drillDownData?.label || ''}`}
        subtitle={drillDownData?.period ? `${drillDownData.period.start} to ${drillDownData.period.end}` : undefined}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDrillDownData(null)}>Close</Button>
            <Button
              as={Link}
              to={`/accounting/reports/income-statement?period=custom&start=${drillDownData?.period?.start}&end=${drillDownData?.period?.end}`}
              variant="primary"
              onClick={() => setDrillDownData(null)}
            >
              View Full Report
            </Button>
          </>
        }
      >
        {drillDownData && (
              drillDownData.loading ? (
                <Spinner label="Loading detailed data..." />
              ) : drillDownData.error ? (
                <ErrorBanner message={`Error: ${drillDownData.error}`} />
              ) : (
                <>
                  {/* Summary Cards */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 16,
                    marginBottom: 24
                  }}>
                    <div style={{
                      padding: '16px',
                      background: '#eff6ff',
                      borderRadius: '6px',
                      border: '1px solid #dbeafe'
                    }}>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: 4 }}>Revenue</div>
                      <div style={{ fontSize: '24px', fontWeight: 600, color: '#1e40af' }}>
                        {Number(drillDownData.plBars?.revenue || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </div>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: '#fef2f2',
                      borderRadius: '6px',
                      border: '1px solid #fecaca'
                    }}>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: 4 }}>Cost of revenue</div>
                      <div style={{ fontSize: '24px', fontWeight: 600, color: '#991b1b' }}>
                        {Number(drillDownData.plBars?.cogs || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </div>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: '#f0fdf4',
                      borderRadius: '6px',
                      border: '1px solid #bbf7d0'
                    }}>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: 4 }}>Gross Profit</div>
                      <div style={{ fontSize: '24px', fontWeight: 600, color: '#166534' }}>
                        {Number(drillDownData.plBars?.gross_profit || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Income statement (GAAP), as on the Reports page */}
                  {drillDownData.income && (
                    <div style={{ marginBottom: 24 }}>
                      <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: 12 }}>
                        Income statement
                      </h4>
                      <StatementTable
                        rows={incomeRows(drillDownData.income)}
                        currentLabel="This period"
                        glRange={[drillDownData.period.start, drillDownData.period.end]}
                      />
                    </div>
                  )}

                  {/* Top Products for Period */}
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: 12 }}>
                      Top 10 Products (This Period)
                    </h4>
                    <div style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      overflow: 'hidden'
                    }}>
                      <table className="table table-sm" style={{ margin: 0 }}>
                        <thead style={{ background: '#f9fafb' }}>
                          <tr>
                            <th>SKU</th>
                            <th>Product</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th style={{ textAlign: 'right' }}>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drillDownData.topProducts?.map((p, i) => (
                            <tr key={i}>
                              <td>{p.sku}</td>
                              <td>{p.name}</td>
                              <td style={{ textAlign: 'right' }}>{p.qty_sold}</td>
                              <td style={{ textAlign: 'right' }}>
                                {Number(p.revenue || 0).toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </td>
                            </tr>
                          ))}
                          {(!drillDownData.topProducts || drillDownData.topProducts.length === 0) && (
                            <tr>
                              <td colSpan="4" className="muted" style={{ textAlign: 'center', padding: 16 }}>
                                No sales data for this period
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )
        )}
      </Modal>
    </div>
    </div>
  )
}
